import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, Inbox, Radio, Send, Calendar, Coins, Settings,
  Users, ShieldCheck, FileText, LogOut, MessageSquare, Star, Heart, Mail, LifeBuoy, Archive, Activity, Languages, Layers, MailPlus, Lock, Flag, Gift, CalendarDays, X, Image as ImageIcon, HeartPulse, Sparkles, AlertTriangle, Percent, Banknote, Tag, Wallet, Bell, BellRing, Undo2, CreditCard, User, Megaphone,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../Navbar";
import NotificationBell from "../NotificationBell";
import ImpersonationBanner from "../ImpersonationBanner";
import CommandPalette from "../CommandPalette";
import HeaderProfileMenu from "./HeaderProfileMenu";
import SystemHealthBanner from "../admin/SystemHealthBanner";
import { Search } from "lucide-react";
import api from "../../lib/api";
import { useDashT } from "../../lib/dashboardStrings";
import { useLang } from "../../context/LanguageContext";
import HamburgerIcon from "../HamburgerIcon";
import DashboardErrorBoundary from "../DashboardErrorBoundary";
import SafeShellPart from "../SafeShellPart";
import MobileBottomNav from "./MobileBottomNav";
import { setRobots } from "../../lib/seo";

const EmailVerifyBanner = () => {
  const dt = useDashT();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("email_banner_dismissed") === "1");
  const [sent, setSent] = useState(false);
  if (!user || user.role === "admin" || user.email_verified !== false || dismissed) return null;
  const resend = async () => {
    try {
      await api.post("/auth/resend-verification");
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    } catch (e) { toast.error("Could not resend. Please try again."); }
  };
  const dismiss = () => { sessionStorage.setItem("email_banner_dismissed", "1"); setDismissed(true); };
  return (
    <div className="bg-[#FF5C00] text-white px-6 py-3 flex items-center justify-between gap-4 flex-wrap" data-testid="email-verify-banner">
      <span className="text-[14px] font-medium">{dt("Please confirm your email address. Check your inbox for a link from Talentsio.")}</span>
      <div className="flex items-center gap-3">
        <button onClick={resend} data-testid="banner-resend" className="text-[13px] font-semibold bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5 transition-colors">
          {sent ? dt("Sent!") : dt("Resend email")}
        </button>
        <button onClick={dismiss} data-testid="banner-dismiss" className="text-white/80 hover:text-white"><X size={18} /></button>
      </div>
    </div>
  );
};

const NAV = {
  client: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/client/events", label: "My events", icon: Calendar },
    { to: "/offers", label: "Offers", icon: Inbox },
    { to: "/client/talents", label: "Talents", icon: Heart },
    { to: "/client/bookings", label: "Bookings", icon: Calendar },
    { to: "/client/invoices", label: "Invoices", icon: FileText },
    { to: "/client/billing", label: "Billing & payments", icon: CreditCard },
    { to: "/messages", label: "Messages", icon: MessageSquare },
    { to: "/notifications", label: "Notifications", icon: Bell },
    { to: "/client/settings", label: "Settings", icon: Settings },
    { to: "/support", label: "Support", icon: LifeBuoy },
    { to: "/account/privacy", label: "Account & privacy", icon: Lock },
  ],
  talent: [
    // Overview — first thing the talent opens.
    { section: "Overview", links: [
      { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
      { to: "/notifications", label: "Notifications", icon: Bell },
    ]},
    // Work — recurring, several times a day.
    { section: "Work", links: [
      { to: "/talent/gigs", label: "Gigs", icon: Radio },
      { to: "/talent/offers", label: "My Offers", icon: Send },
      { to: "/offers", label: "Offers received", icon: Inbox },
      { to: "/talent/bookings", label: "Bookings", icon: Calendar },
      { to: "/talent/availability", label: "Availability", icon: CalendarDays },
      { to: "/messages", label: "Messages", icon: MessageSquare },
      { to: "/talent/new-request", label: "New request", icon: MailPlus },
    ]},
    // Money — daily / weekly.
    { section: "Money", links: [
      { to: "/talent/earnings", label: "Earnings", icon: Wallet },
      { to: "/credits", label: "Credits", icon: Coins },
      { to: "/talent/settings/payments", label: "Payments", icon: Banknote },
      { to: "/talent/invoices", label: "Invoices", icon: FileText },
      { to: "/talent/customers", label: "Customers", icon: Users },
      { to: "/talent/settings/tax", label: "Tax & reporting", icon: Percent },
    ]},
    // Profile & brand — weekly polish.
    { section: "Profile", links: [
      { to: "/talent/settings", label: "Profile editor", icon: User },
      { to: "/talent/media", label: "Media", icon: ImageIcon },
      { to: "/talent/pricing", label: "Pricing & packages", icon: Tag },
    ]},
    // Account — rare but important.
    { section: "Account", links: [
      { to: "/talent/settings/notifications", label: "Settings", icon: Settings },
      { to: "/referrals", label: "Refer & earn", icon: Gift },
      { to: "/support", label: "Support", icon: LifeBuoy },
    ]},
  ],
  agency: [
    { section: "Overview", links: [
      { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
      { to: "/agency/roster", label: "Roster", icon: Users },
      { to: "/notifications", label: "Notifications", icon: Bell },
    ]},
    { section: "Work", links: [
      { to: "/talent/gigs", label: "Gigs", icon: Radio },
      { to: "/talent/offers", label: "Offers", icon: Send },
      { to: "/offers", label: "Offers received", icon: Inbox },
      { to: "/talent/bookings", label: "Bookings", icon: Calendar },
      { to: "/talent/availability", label: "Availability", icon: CalendarDays },
      { to: "/messages", label: "Messages", icon: MessageSquare },
      { to: "/talent/new-request", label: "New request", icon: MailPlus },
    ]},
    { section: "Money", links: [
      { to: "/talent/earnings", label: "Earnings", icon: Wallet },
      { to: "/credits", label: "Credits", icon: Coins },
      { to: "/talent/settings/payments", label: "Payments", icon: Banknote },
      { to: "/talent/invoices", label: "Invoices", icon: FileText },
      { to: "/talent/customers", label: "Customers", icon: Users },
      { to: "/talent/settings/tax", label: "Tax & reporting", icon: Percent },
    ]},
    { section: "Profile", links: [
      { to: "/agency/settings", label: "Agency profile", icon: User },
      { to: "/talent/media", label: "Media", icon: ImageIcon },
      { to: "/talent/pricing", label: "Pricing & packages", icon: Tag },
    ]},
    { section: "Account", links: [
      { to: "/talent/settings/notifications", label: "Settings", icon: Settings },
      { to: "/referrals", label: "Refer & earn", icon: Gift },
      { to: "/support", label: "Support", icon: LifeBuoy },
    ]},
  ],
  admin: [
    // Overview — first thing admin opens each morning.
    { section: "Overview", links: [
      { to: "/dashboard", label: "Command center", icon: LayoutDashboard, end: true },
      { to: "/admin/attention", label: "To do", icon: AlertTriangle },
      { to: "/admin/reminders", label: "Reminders", icon: BellRing },
    ]},
    // Trust & Safety always beats money in a two-sided marketplace — an open
    // dispute blocks payouts.
    { section: "Trust & Safety", links: [
      { to: "/admin/verification", label: "Verification", icon: ShieldCheck },
      { to: "/admin/disputes", label: "Disputes", icon: Flag },
      { to: "/admin/moderation", label: "Moderation", icon: Flag },
      { to: "/admin/reviews", label: "Reviews", icon: Star },
      { to: "/admin/support", label: "Support", icon: LifeBuoy },
    ]},
    // Recurring market operations — several times a day.
    { section: "Market", links: [
      { to: "/admin/leads", label: "Leads", icon: FileText },
      { to: "/admin/offers", label: "Offers", icon: Send },
      { to: "/admin/bookings", label: "Bookings", icon: Calendar },
      { to: "/admin/messages", label: "Messages", icon: MessageSquare },
    ]},
    // Money — daily payouts + refunds.
    { section: "Money", links: [
      { to: "/admin/payments", label: "Payments", icon: Coins },
      { to: "/admin/refunds", label: "Refunds", icon: Undo2 },
      { to: "/admin/invoicing", label: "Invoicing", icon: FileText },
      { to: "/admin/invoices", label: "Platform invoices", icon: FileText },
      { to: "/admin/commissions", label: "Commissions", icon: Percent },
      { to: "/admin/pricing", label: "Pricing", icon: Tag },
    ]},
    // Users — not every minute but the weekly grunt work.
    { section: "Users", links: [
      { to: "/admin/talents", label: "Talents", icon: ShieldCheck },
      { to: "/admin/users", label: "Users", icon: Users },
      { to: "/admin/invitations", label: "Invitations", icon: MailPlus },
    ]},
    // Growth — planned weekly, not reactive.
    { section: "Growth", links: [
      { to: "/admin/spotlight", label: "Spotlight", icon: Sparkles },
      { to: "/admin/seo", label: "SEO", icon: Search },
      { to: "/admin/ads", label: "Google Ads", icon: Megaphone },
      { to: "/AdminAIInsights", label: "AI Insights", icon: Sparkles },
      { to: "/admin/email-templates", label: "Email templates", icon: MailPlus },
      { to: "/admin/email-logs", label: "Email log", icon: Mail },
    ]},
    // Content — monthly.
    { section: "Content", links: [
      { to: "/admin/cms", label: "Content blocks", icon: Layers },
      { to: "/admin/media", label: "Media", icon: ImageIcon },
      { to: "/admin/translations", label: "Translations", icon: Languages },
    ]},
    // Compliance — monthly / reporting dates.
    { section: "Compliance", links: [
      { to: "/admin/compliance/dac7", label: "DAC7 & tax", icon: ShieldCheck },
      { to: "/admin/activity", label: "Audit log", icon: Activity },
      { to: "/admin/data-integrity", label: "Data Integrity", icon: HeartPulse },
      { to: "/admin/archive", label: "Data & Archive", icon: Archive },
    ]},
    // System — rare, last.
    { section: "System", links: [
      { to: "/admin/settings", label: "Settings", icon: Settings },
      { to: "/admin/security", label: "Security", icon: ShieldCheck },
      { to: "/admin/settings/roles", label: "Admin roles", icon: ShieldCheck },
      { to: "/admin/error-reports", label: "Error reports", icon: AlertTriangle },
    ]},
  ],
};

const SidebarLangToggle = () => {
  const { lang, setLang } = useLang();
  const { user } = useAuth();
  const dt = useDashT();
  const change = async (v) => {
    if (v === lang) return;
    setLang(v);
    try {
      if (user?.role === "client") await api.patch("/client/settings", { language: v });
      else if (user?.role === "talent" || user?.role === "agency") await api.put("/talents/me", { language: v });
    } catch { /* noop */ }
  };
  return (
    <div className="flex items-center justify-between px-3 py-2" data-testid="dash-lang-toggle">
      <span className="text-[13px] text-neutral-500 flex items-center gap-2"><Languages size={15} /> {dt("Language")}</span>
      <div className="inline-flex rounded-lg border border-[#2A2A2A] overflow-hidden">
        {["en", "sv"].map((v) => (
          <button key={v} onClick={() => change(v)} data-testid={`dash-lang-${v}`}
            className={`px-2.5 h-7 text-[11px] font-bold uppercase transition-colors ${lang === v ? "bg-[#1A8A4A] text-white" : "text-neutral-500 hover:text-white"}`}>{v}</button>
        ))}
      </div>
    </div>
  );
};

const DashboardLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const dt = useDashT();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const items = NAV[user?.role] || NAV.client;
  const isGrouped = items.length > 0 && items[0].section;
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!user || user.role === "admin") return undefined;
    let alive = true;
    const load = () => api.get("/messages/unread").then(({ data }) => alive && setUnread(data.unread || 0)).catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    const onRead = () => load();
    window.addEventListener("messages_read", onRead);
    return () => { alive = false; clearInterval(iv); window.removeEventListener("messages_read", onRead); };
  }, [user]);

  const doLogout = async () => { await logout(); navigate("/"); };

  // Private views must never be indexed.
  useEffect(() => { setRobots("noindex, nofollow"); return () => setRobots("index, follow"); }, []);

  // Keep the currently-active sidebar link centered in every mounted sidebar
  // (desktop rail + mobile drawer). We use a **callback ref** on the <nav>
  // instead of a useEffect keyed on route/open, because `SidebarInner` is
  // defined inline inside this component: any parent re-render (e.g. the
  // background `unread` message-count poll every 30s or a `messages_read`
  // event) creates a new component identity, unmounts and remounts the nav,
  // and resets scrollTop to 0. A callback ref fires on every mount, so we
  // re-center right after each remount — no jump back below the fold.
  //
  // IMPORTANT: We adjust `nav.scrollTop` directly instead of
  // `el.scrollIntoView()`. scrollIntoView bubbles the scroll up to EVERY
  // ancestor (including the page body), which would yank the main content
  // back to the top whenever the user clicks a menu item. Scoped scroll
  // keeps the effect strictly inside the sidebar.
  const scrollActiveIntoView = (nav) => {
    if (!nav) return;
    const run = () => {
      const el = nav.querySelector('[aria-current="page"]');
      if (!el) return;
      // Position the active item's vertical centre at the nav's vertical centre.
      // offsetTop is measured against the nearest positioned ancestor; the
      // <nav> itself is the scroll container so this is safe.
      const target = el.offsetTop + el.offsetHeight / 2 - nav.clientHeight / 2;
      const max = nav.scrollHeight - nav.clientHeight;
      const next = Math.max(0, Math.min(target, max));
      if (Math.abs(nav.scrollTop - next) > 1) nav.scrollTop = next;
    };
    // Run immediately AND across two rAFs so we win against
    //   (1) the drawer's open transition,
    //   (2) NavLink setting aria-current on a later frame,
    //   (3) the browser resetting scrollTop after layout.
    run();
    requestAnimationFrame(() => { run(); requestAnimationFrame(run); });
  };

  const NavItem = (i) => (
    <NavLink key={i.to} to={i.to} end={i.end} onClick={() => setOpen(false)}
      className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] transition-colors ${isActive ? "bg-[#FF5C00]/12 text-[#FF7A2F]" : "text-neutral-400 hover:text-white hover:bg-[#181818]"}`}>
      <i.icon size={18} /> <span className="flex-1">{dt(i.label)}</span>
      {i.to === "/messages" && unread > 0 && (
        <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF5C00] text-white text-[11px] font-bold flex items-center justify-center" data-testid="nav-unread-badge">{unread}</span>
      )}
    </NavLink>
  );

  const SidebarInner = () => (
    <>
      <div className="px-5 h-[68px] flex items-center border-b border-[#1e1e1e]"><Logo /></div>
      <div className="px-3 pt-4">
        <form onSubmit={(e) => { e.preventDefault(); const q = e.target.q.value.trim(); setOpen(false); navigate(q ? `/talents?search=${encodeURIComponent(q)}` : "/talents"); }} className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input name="q" placeholder={dt("Search...")} data-testid="sidebar-search"
            className="w-full h-10 pl-9 pr-3 rounded-lg bg-[#111111] border border-[#2A2A2A] text-white text-[14px] placeholder-neutral-600 focus:outline-none focus:border-[#FF5C00] transition-colors" />
        </form>
      </div>
      <nav ref={scrollActiveIntoView} data-nav-container="sidebar" className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
        {isGrouped
          ? items.map((group) => (
              <div key={group.section} className="mb-3">
                <div className="px-3 pb-1 text-[11px] uppercase tracking-wider text-neutral-600 font-semibold">{dt(group.section)}</div>
                {group.links.map(NavItem)}
              </div>
            ))
          : items.map(NavItem)}
      </nav>
      <div className="px-3 py-4 border-t border-[#1e1e1e]">
        {user?.role !== "admin" && <div className="mb-2"><SidebarLangToggle /></div>}
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-9 h-9 rounded-full bg-[#FF5C00]/15 flex items-center justify-center text-[#FF7A2F] font-semibold">{(user?.name || user?.email || "?")[0]?.toUpperCase()}</div>
          <div className="min-w-0"><div className="text-[14px] text-white truncate">{user?.name || "User"}</div><div className="text-[12px] text-neutral-500 capitalize">{user?.role}</div></div>
        </div>
        <button onClick={doLogout} className="mt-2 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] text-neutral-400 hover:text-white hover:bg-[#181818] transition-colors"><LogOut size={18} /> {dt("Sign out")}</button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex">
      <aside className="hidden lg:flex w-[248px] flex-col fixed inset-y-0 border-r border-[#1e1e1e] bg-[#0D0D0D]">
        <SafeShellPart label="sidebar-desktop"><SidebarInner /></SafeShellPart>
      </aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute top-0 right-0 h-full w-[280px] flex flex-col border-l border-[#1e1e1e] bg-[#0D0D0D]">
            <SafeShellPart label="sidebar-mobile"><SidebarInner /></SafeShellPart>
          </aside>
        </div>
      )}
      <div className="flex-1 lg:ml-[248px] min-w-0">
        <SafeShellPart label="impersonation-banner"><ImpersonationBanner /></SafeShellPart>
        <SafeShellPart label="command-palette"><CommandPalette /></SafeShellPart>
        <header className="relative h-14 lg:h-[68px] border-b border-[#1e1e1e] flex items-center px-4 lg:px-6 sticky top-0 bg-[#0D0D0D]/90 backdrop-blur z-50">
          {/* Mobile: avatar left, logo center, bell + hamburger right */}
          <div className="lg:hidden flex items-center justify-between w-full">
            <SafeShellPart label="profile-menu-mobile"><HeaderProfileMenu /></SafeShellPart>
            <div className="absolute left-1/2 -translate-x-1/2 [&_img]:!h-7"><Logo /></div>
            <div className="flex items-center gap-1">
              <SafeShellPart label="notif-bell-mobile"><NotificationBell /></SafeShellPart>
              <button className="text-white p-1" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="mobile-menu-btn"><HamburgerIcon /></button>
            </div>
          </div>
          {/* Desktop: title left, search + bell + profile right */}
          <div className="hidden lg:flex items-center justify-between w-full">
            <h1 className="text-[20px] font-semibold text-white">{dt(title)}</h1>
            <div className="flex items-center gap-3">
              <button onClick={() => window.dispatchEvent(new CustomEvent("talentsio:openPalette"))} data-testid="header-search"
                className="flex items-center gap-2 text-[13px] text-neutral-500 bg-[#141414] border border-[#2A2A2A] rounded-lg pl-3 pr-2 py-2 hover:border-[#3A3A3A] hover:text-neutral-300 transition-colors w-[220px]">
                <Search size={15} />
                <span className="flex-1 text-left">{dt("Search...")}</span>
                <kbd className="text-[11px] text-neutral-500 border border-[#2A2A2A] rounded px-1.5 py-0.5">⌘K</kbd>
              </button>
              <SafeShellPart label="notif-bell-desktop"><NotificationBell /></SafeShellPart>
              <SafeShellPart label="profile-menu-desktop"><HeaderProfileMenu /></SafeShellPart>
            </div>
          </div>
        </header>
        <SafeShellPart label="email-verify-banner"><EmailVerifyBanner /></SafeShellPart>
        <SafeShellPart label="system-health-banner"><SystemHealthBanner /></SafeShellPart>
        <main
          className="px-4 lg:px-6 pt-2 lg:pt-4 max-w-[1100px]"
          style={{ paddingBottom: "max(1.5rem, calc(6.5rem + env(safe-area-inset-bottom, 0px)))" }}
        >
          <DashboardErrorBoundary resetKey={location.pathname}>{children}</DashboardErrorBoundary>
        </main>
      </div>
      <SafeShellPart label="mobile-bottom-nav"><MobileBottomNav /></SafeShellPart>
    </div>
  );
};

export default DashboardLayout;
